import React, { useState } from 'react';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { 
  MapPin, Award, Target, DollarSign, Eye, EyeOff, 
  Save, AlertCircle, Gift, Zap
} from 'lucide-react';

const AdminBadgeCreator = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    icon_url: '🏅',
    badge_type: 'location', // location, activity, collection, embed
    trigger_type: 'none', // none, embed
    embed_allowed_domains: '*',
    target_latitude: '',
    target_longitude: '',
    geofence_radius_meters: 50,
    venue_name: '',
    venue_category: '',
    hint_text: '',
    // Activity criteria
    required_posts_count: '',
    required_login_days: '',
    required_comments_count: '',
    required_votes_count: '',
    // Supply & rewards
    max_supply: '',
    show_scarcity: false,
    reveal_location_after_claim: false,
    reward_points: 10,
    is_sponsored: false,
    sponsor_name: '',
    // New fields
    rarity: 'standard',
    status: 'active',
    is_public: true,
    display_order: 0,
    // Collection & Category
    required_badge_ids: [],
    category: 'general',
    is_yearly_challenge: false
  });
  
  const [creating, setCreating] = useState(false);
  const [coordinateMode, setCoordinateMode] = useState('manual'); // 'manual' or 'search'
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconMode, setIconMode] = useState('emoji'); // 'emoji' or 'upload'
  const [availableBadges, setAvailableBadges] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Quick coordinate presets for Regina landmarks
  const reginaLandmarks = [
    { name: 'Regina Public Library (Central)', lat: 50.4452, lng: -104.6189 },
    { name: 'Legislative Building', lat: 50.4429, lng: -104.6178 },
    { name: 'Wascana Park', lat: 50.4346, lng: -104.6278 },
    { name: 'Cornwall Centre', lat: 50.4500, lng: -104.6100 },
    { name: 'Victoria Park', lat: 50.4489, lng: -104.6147 },
    { name: 'RCMP Heritage Centre', lat: 50.4294, lng: -104.6194 },
    { name: 'Mosaic Stadium', lat: 50.4515, lng: -104.5928 },
    { name: 'City Hall', lat: 50.4460, lng: -104.6090 }
  ];

  const iconOptions = ['🏅', '🏆', '⭐', '🎯', '🎖️', '👑', '🔥', '💎', '🌟', '✨', '🎁', '☕', '🍔', '🏛️', '🏞️', '📚', '🎨', '🎭', '🎪', '🎢'];

  // Load available badges and categories on mount
  React.useEffect(() => {
    const loadData = async () => {
      try {
        const [badgesRes, categoriesRes] = await Promise.all([
          api.get('/admin/badges'),
          api.get('/badges/categories')
        ]);
        
        setAvailableBadges(badgesRes.data.badges || []);
        setCategories(categoriesRes.data.categories || []);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load badges and categories');
      } finally {
        setLoadingData(false);
      }
    };
    
    loadData();
  }, []);

  if (!isAdmin()) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-headline text-2xl text-white mb-2">Access Denied</h2>
            <p className="font-mono text-sm text-gray-500">Admin access required</p>
          </div>
        </div>
      </Layout>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.code || !formData.name) {
      toast.error('Code and Name are required');
      return;
    }
    
    if (formData.badge_type === 'location' && (!formData.target_latitude || !formData.target_longitude)) {
      toast.error('Location badges require coordinates');
      return;
    }
    
    // Embed badges don't need location coords or activity conditions - just code + name + trigger_type
    
    setCreating(true);
    
    try {
      const payload = {
        ...formData,
        trigger_type: formData.badge_type === 'embed' ? 'embed' : (formData.trigger_type || 'none'),
        target_latitude: formData.target_latitude ? parseFloat(formData.target_latitude) : null,
        target_longitude: formData.target_longitude ? parseFloat(formData.target_longitude) : null,
        geofence_radius_meters: parseInt(formData.geofence_radius_meters) || 50,
        max_supply: formData.max_supply ? parseInt(formData.max_supply) : null,
        reward_points: parseInt(formData.reward_points) || 0,
        // Convert empty strings to null for all optional int fields
        required_posts_count: formData.required_posts_count ? parseInt(formData.required_posts_count) : null,
        required_login_days: formData.required_login_days ? parseInt(formData.required_login_days) : null,
        required_comments_count: formData.required_comments_count ? parseInt(formData.required_comments_count) : null,
        required_votes_count: formData.required_votes_count ? parseInt(formData.required_votes_count) : null,
        required_badge_ids: formData.required_badge_ids?.length ? formData.required_badge_ids : null,
      };
      
      await api.post('/admin/badges/create', payload);
      
      toast.success(`Badge "${formData.name}" created successfully!`);
      navigate('/admin?tab=badges');
      
      // Reset form
      setFormData({
        code: '',
        name: '',
        description: '',
        icon_url: '🏅',
        badge_type: 'location',
        trigger_type: 'none',
        embed_allowed_domains: '*',
        target_latitude: '',
        target_longitude: '',
        geofence_radius_meters: 50,
        venue_name: '',
        venue_category: '',
        hint_text: '',
        required_posts_count: '',
        required_login_days: '',
        required_comments_count: '',
        required_votes_count: '',
        max_supply: '',
        show_scarcity: false,
        reveal_location_after_claim: false,
        reward_points: 10,
        is_sponsored: false,
        sponsor_name: '',
        rarity: 'standard',
        status: 'active',
        is_public: true,
        display_order: 0,
        required_badge_ids: [],
        category: 'general',
        is_yearly_challenge: false
      });
    } catch (error) {
      console.error('Error creating badge:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create badge';
      toast.error(errorMsg, { autoClose: 10000 }); // 10 seconds to read error
    } finally {
      setCreating(false);
    }
  };

  const setLandmarkCoordinates = (landmark) => {
    setFormData(prev => ({
      ...prev,
      target_latitude: landmark.lat.toString(),
      target_longitude: landmark.lng.toString(),
      venue_name: landmark.name
    }));
    toast.success(`Coordinates set for ${landmark.name}`);
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB');
      return;
    }

    setUploadingIcon(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/admin/badges/upload-icon', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData(prev => ({
        ...prev,
        icon_url: response.data.icon_url
      }));

      toast.success('Badge icon uploaded successfully!');
    } catch (error) {
      console.error('Error uploading icon:', error);
      toast.error('Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Award className="w-8 h-8 text-[#C5A52A]" />
              <h1 className="font-headline text-4xl text-white">CREATE BADGE</h1>
            </div>
            <p className="font-mono text-sm text-gray-500">
              Create location-based badges for users to earn
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Basic Info */}
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
              <h3 className="font-headline text-xl text-white mb-4">BASIC INFO</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Code */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Badge Code * (unique ID)
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    placeholder="library-visitor"
                    required
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Badge Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    placeholder="Library Visitor"
                    required
                  />
                </div>
              </div>

              {/* Description */}
              <div className="mt-4">
                <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  placeholder="Visit the Regina Public Library to earn this badge"
                  rows={3}
                />
              </div>

              {/* Icon */}
              <div className="mt-4">
                <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                  Icon (emoji or upload image)
                </label>
                
                {/* Icon Mode Toggle */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setIconMode('emoji')}
                    className={`px-4 py-2 rounded font-mono text-sm transition-colors ${
                      iconMode === 'emoji'
                        ? 'bg-[#C5A52A] text-black'
                        : 'bg-[#111] border border-[#2a2a2a] text-gray-400 hover:border-[#C5A52A]'
                    }`}
                  >
                    Emoji/URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setIconMode('upload')}
                    className={`px-4 py-2 rounded font-mono text-sm transition-colors ${
                      iconMode === 'upload'
                        ? 'bg-[#C5A52A] text-black'
                        : 'bg-[#111] border border-[#2a2a2a] text-gray-400 hover:border-[#C5A52A]'
                    }`}
                  >
                    Upload Image
                  </button>
                </div>

                {iconMode === 'emoji' ? (
                  <div className="space-y-3">
                    {/* Icon Input & Preview */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.icon_url}
                        onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                        className="flex-1 px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                        placeholder="🏅 or https://your-site.com/icon.png"
                      />
                      <div className="text-4xl px-4 py-2 bg-[#111] border border-[#2a2a2a] rounded">
                        {formData.icon_url}
                      </div>
                    </div>

                    {/* Image Size Info */}
                    <div className="bg-blue-900/20 border border-blue-900/50 rounded p-3">
                    <p className="font-mono text-xs text-blue-400 mb-1">
                      📐 Custom Icon Specs:
                    </p>
                    <ul className="font-mono text-xs text-blue-300 space-y-1">
                      <li>• Recommended size: <strong>256x256px</strong> (or 128x128px minimum)</li>
                      <li>• Format: PNG or SVG with transparent background</li>
                      <li>• Upload to your image host, then paste URL above</li>
                    </ul>
                  </div>

                  {/* Quick Emoji Picker */}
                  <div className="flex flex-wrap gap-2">
                    {iconOptions.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon_url: emoji })}
                        className="text-2xl hover:scale-125 transition-transform"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Upload Button */}
                    <div>
                      <input
                        type="file"
                        id="icon-upload"
                        accept="image/*"
                        onChange={handleIconUpload}
                        className="hidden"
                      />
                      <label
                        htmlFor="icon-upload"
                        className={`block w-full px-4 py-8 bg-[#111] border-2 border-dashed ${
                          uploadingIcon ? 'border-yellow-500' : 'border-[#2a2a2a] hover:border-[#C5A52A]'
                        } rounded cursor-pointer text-center transition-colors`}
                      >
                        {uploadingIcon ? (
                          <div className="text-yellow-500 font-mono text-sm">
                            Uploading...
                          </div>
                        ) : (
                          <div>
                            <div className="text-4xl mb-2">📤</div>
                            <div className="font-mono text-sm text-gray-400">
                              Click to upload badge icon
                            </div>
                            <div className="font-mono text-xs text-gray-600 mt-1">
                              PNG, JPG, GIF, SVG • Max 5MB
                            </div>
                          </div>
                        )}
                      </label>
                    </div>

                    {/* Preview */}
                    {formData.icon_url && formData.icon_url !== '🏅' && (
                      <div className="bg-[#111] border border-[#2a2a2a] rounded p-4">
                        <div className="font-mono text-xs text-gray-500 mb-2">PREVIEW:</div>
                        <div className="flex items-center gap-4">
                          <img
                            src={formData.icon_url}
                            alt="Badge icon"
                            className="w-16 h-16 object-contain bg-gray-900 rounded"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="flex-1 font-mono text-xs text-gray-400 break-all">
                            {formData.icon_url}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* New Fields: Rarity, Status, Visibility */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Rarity */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Badge Rarity
                  </label>
                  <select
                    value={formData.rarity}
                    onChange={(e) => setFormData({ ...formData, rarity: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  >
                    <option value="standard">Standard (Common)</option>
                    <option value="special">Special (Rare)</option>
                    <option value="official">Official (Councillor/Mayor)</option>
                    <option value="legacy">Legacy (Former roles)</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  >
                    <option value="active">Active (Being awarded)</option>
                    <option value="inactive">Inactive (Not awarded)</option>
                    <option value="hidden">Hidden (Admin only)</option>
                  </select>
                </div>

                {/* Public Visibility */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Public Visibility
                  </label>
                  <select
                    value={formData.is_public ? 'public' : 'mystery'}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.value === 'public' })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  >
                    <option value="public">Public (Shows on badge legend)</option>
                    <option value="mystery">Mystery (Hidden until earned)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Badge Type Selection */}
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
              <h3 className="font-headline text-xl text-white mb-4">BADGE TYPE</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Location Badge */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, badge_type: 'location', trigger_type: 'none' })}
                  className={`p-4 rounded border-2 transition-all text-left ${
                    formData.badge_type === 'location'
                      ? 'border-[#C5A52A] bg-[#C5A52A]/10'
                      : 'border-[#2a2a2a] hover:border-[#3a3a3a]'
                  }`}
                >
                  <div className="text-2xl mb-2">📍</div>
                  <div className="font-mono text-sm text-white mb-1">Location Badge</div>
                  <div className="font-mono text-xs text-gray-500">
                    Check in at a specific place
                  </div>
                </button>

                {/* Activity Badge */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, badge_type: 'activity', trigger_type: 'none' })}
                  className={`p-4 rounded border-2 transition-all text-left ${
                    formData.badge_type === 'activity'
                      ? 'border-[#C5A52A] bg-[#C5A52A]/10'
                      : 'border-[#2a2a2a] hover:border-[#3a3a3a]'
                  }`}
                >
                  <div className="text-2xl mb-2">🎯</div>
                  <div className="font-mono text-sm text-white mb-1">Activity Badge</div>
                  <div className="font-mono text-xs text-gray-500">
                    Complete actions (posts, logins, etc.)
                  </div>
                </button>

                {/* Collection Badge */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, badge_type: 'collection', trigger_type: 'none' })}
                  className={`p-4 rounded border-2 transition-all text-left ${
                    formData.badge_type === 'collection'
                      ? 'border-[#C5A52A] bg-[#C5A52A]/10'
                      : 'border-[#2a2a2a] hover:border-[#3a3a3a]'
                  }`}
                >
                  <div className="text-2xl mb-2">🏆</div>
                  <div className="font-mono text-sm text-white mb-1">Collection Badge</div>
                  <div className="font-mono text-xs text-gray-500">
                    Earn by collecting other badges
                  </div>
                </button>

                {/* Embed / Partner Badge */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, badge_type: 'embed', trigger_type: 'embed' })}
                  className={`p-4 rounded border-2 transition-all text-left ${
                    formData.badge_type === 'embed'
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-[#2a2a2a] hover:border-[#3a3a3a]'
                  }`}
                >
                  <div className="text-2xl mb-2">🔗</div>
                  <div className="font-mono text-sm text-white mb-1">Partner / Embed Badge</div>
                  <div className="font-mono text-xs text-gray-500">
                    Awarded via snippet on any external site — no CYQR conditions required
                  </div>
                </button>
              </div>

              {/* Embed badge config */}
              {formData.badge_type === 'embed' && (
                <div className="mt-6 bg-purple-900/10 border border-purple-500/30 rounded p-5 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-purple-400" />
                    <span className="font-mono text-sm text-purple-300 font-bold uppercase tracking-wider">Partner Embed Settings</span>
                  </div>
                  <p className="font-mono text-xs text-gray-400">
                    This badge is awarded automatically when a logged-in CYQR user visits the partner page that has your snippet installed.
                    No additional conditions are required on the CYQR side.
                  </p>
                  <div>
                    <label className="block font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">
                      Allowed Domains (comma-separated, or * for all)
                    </label>
                    <input
                      type="text"
                      value={formData.embed_allowed_domains}
                      onChange={(e) => setFormData({ ...formData, embed_allowed_domains: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-purple-500/30 text-white font-mono text-sm focus:border-purple-500 focus:outline-none"
                      placeholder="reginacoffee.ca, partnersite.com  (or * for any domain)"
                    />
                    <p className="font-mono text-[10px] text-gray-600 mt-1">
                      Leave * to allow the badge to be awarded from any external site.
                    </p>
                  </div>
                  {formData.code && (
                    <div>
                      <label className="block font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">
                        Snippet to give to the partner — paste before &lt;/body&gt;
                      </label>
                      <div className="bg-[#0a0a0a] border border-purple-500/20 rounded p-4 relative">
                        <pre className="font-mono text-xs text-green-400/80 whitespace-pre-wrap overflow-x-auto leading-relaxed">{`<!-- CYQR Badge: ${formData.name || formData.code} -->
<script>
(function() {
  var token = localStorage.getItem('cyqr_token');
  if (!token) return; // User not logged into CYQR
  fetch('${window.location.origin}/api/badges/embed-claim', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ badge_code: '${formData.code}', source: window.location.href })
  }).then(r => r.json()).then(function(d) {
    if (d.success) { console.log('CYQR: Badge earned -', d.badge && d.badge.name); }
  }).catch(function() {});
})();
</script>`}</pre>
                        <button
                          type="button"
                          onClick={() => {
                            const snippet = `<!-- CYQR Badge: ${formData.name || formData.code} -->\n<script>\n(function() {\n  var token = localStorage.getItem('cyqr_token');\n  if (!token) return;\n  fetch('${window.location.origin}/api/badges/embed-claim', {\n    method: 'POST',\n    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },\n    body: JSON.stringify({ badge_code: '${formData.code}', source: window.location.href })\n  }).then(r => r.json()).then(function(d) {\n    if (d.success) { console.log('CYQR: Badge earned -', d.badge && d.badge.name); }\n  }).catch(function() {});\n})();\n</script>`;
                            navigator.clipboard.writeText(snippet);
                            toast.success('Snippet copied!');
                          }}
                          className="absolute top-3 right-3 text-purple-400 hover:text-purple-300 font-mono text-xs border border-purple-500/30 px-2 py-1"
                        >
                          Copy
                        </button>
                      </div>
                      <p className="font-mono text-[10px] text-yellow-500/70 mt-2">
                        ⚠ Save the badge first, then copy the final snippet. The badge code is locked once saved.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>



            {/* Collection Badge Requirements (shown if collection badge) */}
            {formData.badge_type === 'collection' && (
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5 text-[#C5A52A]" />
                  <h3 className="font-headline text-xl text-white">REQUIRED BADGES</h3>
                </div>

                <div className="bg-blue-900/20 border border-blue-900/50 rounded p-3 mb-4">
                  <p className="font-mono text-xs text-blue-400">
                    Select which badges users must earn to unlock this collection badge.
                  </p>
                </div>

                {loadingData ? (
                  <div className="text-gray-500 font-mono text-sm">Loading badges...</div>
                ) : availableBadges.length === 0 ? (
                  <div className="bg-yellow-900/20 border border-yellow-900/50 rounded p-4">
                    <p className="font-mono text-sm text-yellow-400">
                      No badges available yet. Create some location or activity badges first, then create collection badges that require them.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {availableBadges
                      .filter(b => b.badge_type !== 'collection') // Don't show other collection badges
                      .map((badge) => (
                        <label
                          key={badge.id}
                          className="flex items-center gap-3 p-3 bg-[#111] border border-[#2a2a2a] rounded hover:border-[#C5A52A] cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.required_badge_ids.includes(badge.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  required_badge_ids: [...formData.required_badge_ids, badge.id]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  required_badge_ids: formData.required_badge_ids.filter(id => id !== badge.id)
                                });
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <div className="text-2xl">{badge.icon_url}</div>
                          <div className="flex-1">
                            <div className="font-mono text-sm text-white">{badge.name}</div>
                            <div className="font-mono text-xs text-gray-500">{badge.badge_type}</div>
                          </div>
                        </label>
                      ))}
                  </div>
                )}

                {formData.required_badge_ids.length > 0 && (
                  <div className="mt-4 bg-green-900/20 border border-green-500/50 rounded p-3">
                    <p className="font-mono text-sm text-green-400">
                      ✓ {formData.required_badge_ids.length} badge{formData.required_badge_ids.length !== 1 ? 's' : ''} required
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Category & Organization */}
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
              <h3 className="font-headline text-xl text-white mb-4">ORGANIZATION</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Category */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Badge Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  >
                    <option value="general">General</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Yearly Challenge */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Yearly Challenge
                  </label>
                  <select
                    value={formData.is_yearly_challenge ? 'yes' : 'no'}
                    onChange={(e) => setFormData({ ...formData, is_yearly_challenge: e.target.value === 'yes' })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  >
                    <option value="no">Regular Badge</option>
                    <option value="yes">Yearly Challenge (for leaderboard)</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 bg-purple-900/20 border border-purple-900/50 rounded p-3">
                <p className="font-mono text-xs text-purple-400 mb-2">
                  <strong>Categories:</strong> Organize badges into sections on the badge legend page.
                </p>
                <p className="font-mono text-xs text-purple-400">
                  <strong>Yearly Challenge:</strong> Marks this badge for yearly leaderboard tracking (great for competitions & prizes!).
                </p>
              </div>
            </div>

            {/* Activity Criteria (shown if activity badge) */}
            {formData.badge_type === 'activity' && (
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-[#C5A52A]" />
                  <h3 className="font-headline text-xl text-white">ACTIVITY REQUIREMENTS</h3>
                </div>

                <div className="bg-blue-900/20 border border-blue-900/50 rounded p-3 mb-4">
                  <p className="font-mono text-xs text-blue-400">
                    Set one or more criteria. User must meet ALL criteria to earn badge.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Posts Count */}
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Required Posts
                    </label>
                    <input
                      type="number"
                      value={formData.required_posts_count}
                      onChange={(e) => setFormData({ ...formData, required_posts_count: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                      placeholder="e.g., 100"
                    />
                    <p className="font-mono text-xs text-gray-600 mt-1">Leave empty to skip</p>
                  </div>

                  {/* Login Days */}
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Required Login Days
                    </label>
                    <input
                      type="number"
                      value={formData.required_login_days}
                      onChange={(e) => setFormData({ ...formData, required_login_days: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                      placeholder="e.g., 30"
                    />
                    <p className="font-mono text-xs text-gray-600 mt-1">Consecutive or total days</p>
                  </div>

                  {/* Comments Count */}
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Required Comments
                    </label>
                    <input
                      type="number"
                      value={formData.required_comments_count}
                      onChange={(e) => setFormData({ ...formData, required_comments_count: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                      placeholder="e.g., 50"
                    />
                  </div>

                  {/* Votes Count */}
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Required Votes Cast
                    </label>
                    <input
                      type="number"
                      value={formData.required_votes_count}
                      onChange={(e) => setFormData({ ...formData, required_votes_count: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                      placeholder="e.g., 10"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Location Settings (shown if location badge) */}
            {formData.badge_type === 'location' && (
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-[#C5A52A]" />
                  <h3 className="font-headline text-xl text-white">LOCATION</h3>
                </div>

                {/* Coordinate Helper Tools */}
                <div className="bg-purple-900/20 border border-purple-500/30 rounded p-4 mb-4">
                  <p className="font-mono text-xs text-purple-400 mb-2">
                    🛠️ How to find coordinates:
                  </p>
                  <div className="space-y-2 font-mono text-xs text-purple-300">
                    <div>
                      <strong>Method 1: Google Maps</strong>
                      <ol className="list-decimal list-inside ml-2 mt-1 space-y-1">
                        <li>Open <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="text-[#C5A52A] underline">Google Maps</a></li>
                        <li>Right-click the location</li>
                        <li>Click the coordinates at top (e.g., "50.4452, -104.6189")</li>
                        <li>Coordinates copied! Paste below.</li>
                      </ol>
                    </div>
                    <div>
                      <strong>Method 2: LatLong.net</strong>
                      <ol className="list-decimal list-inside ml-2 mt-1 space-y-1">
                        <li>Visit <a href="https://www.latlong.net" target="_blank" rel="noopener noreferrer" className="text-[#C5A52A] underline">LatLong.net</a></li>
                        <li>Search for address or click map</li>
                        <li>Copy latitude & longitude</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Regina Landmarks Quick Select */}
                <div className="mb-4">
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Quick Select Regina Landmark
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {reginaLandmarks.map(landmark => (
                      <button
                        key={landmark.name}
                        type="button"
                        onClick={() => setLandmarkCoordinates(landmark)}
                        className="text-left px-3 py-2 bg-[#111] border border-[#2a2a2a] rounded hover:border-[#C5A52A] transition-colors font-mono text-xs text-gray-300"
                      >
                        {landmark.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Latitude */}
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Latitude *
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={formData.target_latitude}
                      onChange={(e) => setFormData({ ...formData, target_latitude: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                      placeholder="50.4452"
                    />
                  </div>

                  {/* Longitude */}
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Longitude *
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={formData.target_longitude}
                      onChange={(e) => setFormData({ ...formData, target_longitude: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                      placeholder="-104.6189"
                    />
                  </div>

                  {/* Radius */}
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Geofence Radius (meters)
                    </label>
                    <input
                      type="number"
                      value={formData.geofence_radius_meters}
                      onChange={(e) => setFormData({ ...formData, geofence_radius_meters: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    />
                    <p className="font-mono text-xs text-gray-600 mt-1">Default: 50m (~164 feet)</p>
                  </div>
                </div>

                {/* Venue Name & Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Venue Name
                    </label>
                    <input
                      type="text"
                      value={formData.venue_name}
                      onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                      placeholder="Regina Public Library"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                      Category
                    </label>
                    <select
                      value={formData.venue_category}
                      onChange={(e) => setFormData({ ...formData, venue_category: e.target.value })}
                      className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    >
                      <option value="">Select category</option>
                      <option value="civic">Civic/Government</option>
                      <option value="library">Library</option>
                      <option value="park">Park/Recreation</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="coffee">Coffee Shop</option>
                      <option value="retail">Retail</option>
                      <option value="entertainment">Entertainment</option>
                      <option value="cultural">Cultural/Arts</option>
                      <option value="sports">Sports</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Mystery Badge Settings */}
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Gift className="w-5 h-5 text-purple-400" />
                <h3 className="font-headline text-xl text-white">MYSTERY BADGE SETTINGS</h3>
              </div>

              {/* Hide Location Toggle */}
              <div className="mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.reveal_location_after_claim}
                    onChange={(e) => setFormData({ ...formData, reveal_location_after_claim: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <div>
                    <span className="font-mono text-sm text-white">Make this a Mystery Badge</span>
                    <p className="font-mono text-xs text-gray-500">
                      Hide location until someone claims it
                    </p>
                  </div>
                </label>
              </div>

              {/* Hint Text */}
              {formData.reveal_location_after_claim && (
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Mystery Hint (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.hint_text}
                    onChange={(e) => setFormData({ ...formData, hint_text: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-purple-500/30 text-white font-mono text-sm focus:border-purple-500 focus:outline-none"
                    placeholder="Where Regina gets caffeinated..."
                  />
                </div>
              )}
            </div>

            {/* Supply & Scarcity */}
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-5 h-5 text-[#C5A52A]" />
                <h3 className="font-headline text-xl text-white">SUPPLY & SCARCITY</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Max Supply */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Max Supply (optional)
                  </label>
                  <input
                    type="number"
                    value={formData.max_supply}
                    onChange={(e) => setFormData({ ...formData, max_supply: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    placeholder="Leave empty for unlimited"
                  />
                  <p className="font-mono text-xs text-gray-600 mt-1">
                    Leave empty for unlimited supply. Set to 1 for mystery badge.
                  </p>
                </div>

                {/* Reward Points */}
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Reward Points
                  </label>
                  <input
                    type="number"
                    value={formData.reward_points}
                    onChange={(e) => setFormData({ ...formData, reward_points: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                  />
                </div>
              </div>

              {/* Show Scarcity Toggle */}
              <div className="mt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_scarcity}
                    onChange={(e) => setFormData({ ...formData, show_scarcity: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <div>
                    <span className="font-mono text-sm text-white">Show Scarcity Display</span>
                    <p className="font-mono text-xs text-gray-500">
                      Display "X/Y remaining" to users (creates urgency!)
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Sponsorship */}
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-[#C5A52A]" />
                <h3 className="font-headline text-xl text-white">SPONSORSHIP</h3>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_sponsored}
                    onChange={(e) => setFormData({ ...formData, is_sponsored: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="font-mono text-sm text-white">This is a sponsored badge</span>
                </label>
              </div>

              {formData.is_sponsored && (
                <div>
                  <label className="block font-mono text-xs text-gray-500 tracking-wider mb-2 uppercase">
                    Sponsor Name (internal tracking only)
                  </label>
                  <input
                    type="text"
                    value={formData.sponsor_name}
                    onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                    placeholder="Business Name"
                  />
                  <p className="font-mono text-xs text-gray-600 mt-1">
                    For admin tracking only - not shown to users
                  </p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={creating}
                className={`flex-1 py-4 px-6 rounded font-headline text-lg transition-all ${
                  creating
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    : 'bg-[#C5A52A] hover:bg-[#D4B93A] text-black'
                }`}
              >
                {creating ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Creating...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Save className="w-5 h-5" />
                    CREATE BADGE
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate('/admin?tab=badges')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AdminBadgeCreator;
